import { FormEvent, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Mail, Lock } from 'lucide-react';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { GoogleAuthButton, AppleAuthButton } from '@/components/auth/OAuthButtons';
import { TextField, PasswordField, OrDivider, FormError, SubmitButton } from '@/components/auth/FormFields';
import { login, verifyMfaLogin, readApiError } from '@/lib/auth-api';

export function LoginPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const loc = useLocation();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // MFA second-factor step: set once login returns { mfaRequired, mfaToken }.
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');

  const redirectTo = (loc.state as { from?: string })?.from || '/app';

  const finish = async (companyId: string | null | undefined) => {
    // Refresh the currentUser cache — every protected page reads from it.
    await qc.invalidateQueries({ queryKey: ['currentUser'] });
    nav(companyId ? redirectTo : '/onboarding', { replace: true });
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (!email) next.email = t('auth.errors.emailRequired');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = t('auth.errors.emailInvalid');
    if (!password) next.password = t('auth.errors.passwordRequired');
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setLoading(true);
    setFormError(null);
    try {
      const res = await login({ email: email.trim(), password });
      if (res.mfaRequired && res.mfaToken) {
        setMfaToken(res.mfaToken); // switch to the code-entry step
        return;
      }
      await finish(res.user?.companyId);
    } catch (err) {
      setFormError(readApiError(err, t('auth.errors.loginFailed')));
    } finally {
      setLoading(false);
    }
  };

  const onMfaSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!mfaToken || !mfaCode.trim()) return;
    setLoading(true);
    setFormError(null);
    try {
      const { user } = await verifyMfaLogin(mfaToken, mfaCode.trim());
      await finish(user.companyId);
    } catch (err) {
      setFormError(readApiError(err, t('auth.mfa.invalidCode', { defaultValue: 'Invalid authentication code' })));
    } finally {
      setLoading(false);
    }
  };

  if (mfaToken) {
    return (
      <AuthLayout
        title={t('auth.mfa.title', { defaultValue: 'Two-factor authentication' })}
        subtitle={t('auth.mfa.subtitle', { defaultValue: 'Enter the 6-digit code from your authenticator app (or a recovery code).' })}
      >
        <form onSubmit={onMfaSubmit} className="space-y-4" noValidate>
          <FormError message={formError} />
          <TextField
            label={t('auth.mfa.code', { defaultValue: 'Authentication code' })}
            type="text"
            inputMode="text"
            autoComplete="one-time-code"
            autoFocus
            placeholder="123456"
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
            leadingIcon={<Lock className="h-4 w-4" />}
            required
          />
          <SubmitButton loading={loading}>{t('auth.mfa.verify', { defaultValue: 'Verify' })}</SubmitButton>
          <button
            type="button"
            onClick={() => { setMfaToken(null); setMfaCode(''); setFormError(null); }}
            className="w-full text-xs font-semibold text-muted-foreground hover:text-foreground"
          >
            {t('auth.mfa.back', { defaultValue: 'Back to sign in' })}
          </button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('auth.login.title')}
      subtitle={t('auth.login.subtitle')}
      footer={
        <p className="text-center">
          {t('auth.login.noAccount')}{' '}
          <Link to="/signup" className="font-semibold text-primary hover:underline">
            {t('auth.login.signupLink')}
          </Link>
        </p>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <GoogleAuthButton disabled={loading} />
          <AppleAuthButton disabled={loading} />
        </div>

        <OrDivider label={t('auth.orEmail')} />

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <FormError message={formError} />

          <TextField
            label={t('auth.fields.email')}
            type="email"
            autoComplete="email"
            placeholder={t('auth.placeholders.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={errors.email}
            leadingIcon={<Mail className="h-4 w-4" />}
            required
          />

          <PasswordField
            label={t('auth.fields.password')}
            autoComplete="current-password"
            placeholder={t('auth.placeholders.password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={errors.password}
            leadingIcon={<Lock className="h-4 w-4" />}
            required
          />

          <div className="flex items-center justify-end">
            <Link to="/forgot-password" className="text-xs font-semibold text-primary hover:underline">
              {t('auth.login.forgotPassword')}
            </Link>
          </div>

          <SubmitButton loading={loading}>{t('auth.login.submit')}</SubmitButton>
        </form>
      </div>
    </AuthLayout>
  );
}
